/**
 * Concrete volume & material estimation per IS 456:2000 / IS 10262:2019 (nominal mixes).
 * All dimensions in metres; output volumes in m³; cement in 50 kg bags (IS).
 */

export const DRY_VOLUME_FACTOR = 1.54;

export const SHAPE_TYPES = [
    { value: 'slab_wall_footing', label: 'Slabs, Square Footings, or Walls' },
    { value: 'round_column_hole', label: 'Hole, Column, or Round Footings' },
    { value: 'circular_slab_tube', label: 'Circular Slab or Tube' },
    { value: 'curb_gutter', label: 'Curb and Gutter Barrier' },
    { value: 'stairs', label: 'Stairs' },
];

export const PROJECT_TYPES = [
    { value: 'individual_house', label: 'Individual House / Villa' },
    { value: 'school', label: 'School / Institution' },
    { value: 'shop', label: 'Shop / Retail' },
    { value: 'commercial_building', label: 'Commercial Building' },
    { value: 'industrial', label: 'Industrial / Factory' },
    { value: 'road', label: 'Road / Pavement' },
    { value: 'dam', label: 'Dam / Reservoir' },
    { value: 'bridge', label: 'Bridge / Flyover' },
    { value: 'other', label: 'Other' },
];

export const CONCRETE_GRADES = [
    { value: 'M10', label: 'M10 (PCC / levelling) — IS 456 Table 9' },
    { value: 'M15', label: 'M15 (PCC) — IS 456' },
    { value: 'M20', label: 'M20 (General RC) — IS 456' },
    { value: 'M25', label: 'M25 (Structural) — IS 10262' },
    { value: 'M30', label: 'M30 (Commercial / heavy load)' },
    { value: 'M35', label: 'M35 (High-rise)' },
    { value: 'M40', label: 'M40 (Dam / special structures)' },
];

/** Nominal cement content kg/m³ (IS 456:2000 Table 9, indicative). */
export const IS_MIX_DESIGN = {
    M10: { cementKgPerM3: 220, sandRatio: 3, aggregateRatio: 6, parts: 10 },
    M15: { cementKgPerM3: 270, sandRatio: 2, aggregateRatio: 4, parts: 7 },
    M20: { cementKgPerM3: 320, sandRatio: 1.5, aggregateRatio: 3, parts: 5.5 },
    M25: { cementKgPerM3: 340, sandRatio: 1, aggregateRatio: 2, parts: 4 },
    M30: { cementKgPerM3: 380, sandRatio: 1, aggregateRatio: 2, parts: 4 },
    M35: { cementKgPerM3: 400, sandRatio: 1, aggregateRatio: 2, parts: 4 },
    M40: { cementKgPerM3: 420, sandRatio: 0.75, aggregateRatio: 1.5, parts: 3.25 },
};

export const CEMENT_BAG_KG = 50;

/** Bulk density (kg/m³) — indicative for site quantity estimates (IS / CPWD practice). */
export const SAND_DENSITY_KG_M3 = 1600;
export const AGGREGATE_DENSITY_KG_M3 = 1500;

/** Length unit options (calculator.net style); values stored in DB, converted to metres for calc. */
export const LENGTH_UNITS = [
    { value: 'm', label: 'meters', toMeters: 1 },
    { value: 'cm', label: 'centimeters', toMeters: 0.01 },
    { value: 'mm', label: 'millimeters', toMeters: 0.001 },
    { value: 'ft', label: 'feet', toMeters: 0.3048 },
    { value: 'in', label: 'inches', toMeters: 0.0254 },
    { value: 'yd', label: 'yards', toMeters: 0.9144 },
];

function num(v) {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
}

export function dimToMeters(value, unit = 'm') {
    const u = LENGTH_UNITS.find((x) => x.value === unit);
    return num(value) * (u?.toMeters ?? 1);
}

function dimM(dims, key) {
    return dimToMeters(dims[key], dims[`${key}_unit`] || 'm');
}

export function calculateShapeVolume(shapeType, dims, quantity = 1) {
    const qty = Math.max(num(quantity), 0);
    if (qty === 0) return 0;

    switch (shapeType) {
    case 'slab_wall_footing': {
        const l = dimM(dims, 'length');
        const w = dimM(dims, 'width');
        const h = dimM(dims, 'height');
        return l * w * h * qty;
    }
    case 'round_column_hole': {
        const d = dimM(dims, 'diameter');
        const h = dimM(dims, 'height');
        const r = d / 2;
        return Math.PI * r * r * h * qty;
    }
    case 'circular_slab_tube': {
        const d1 = dimM(dims, 'outer_diameter');
        const d2 = dimM(dims, 'inner_diameter');
        const h = dimM(dims, 'height');
        return (Math.PI * (d1 * d1 - d2 * d2) / 4) * h * qty;
    }
    case 'curb_gutter': {
        const curbDepth = dimM(dims, 'curb_depth');
        const gutterWidth = dimM(dims, 'gutter_width');
        const curbHeight = dimM(dims, 'curb_height');
        const flagThickness = dimM(dims, 'flag_thickness');
        const length = dimM(dims, 'length');
        const crossSection = (curbDepth * curbHeight)
            + (gutterWidth * flagThickness)
            + (curbDepth * gutterWidth);
        return length * crossSection * qty;
    }
    case 'stairs': {
        const run = dimM(dims, 'run');
        const rise = dimM(dims, 'rise');
        const width = dimM(dims, 'width');
        const platformDepth = dimM(dims, 'platform_depth');
        const numRisers = num(dims.num_risers);
        const stepsVol = numRisers * run * rise * width / 2;
        const platformVol = platformDepth * width * rise;
        return (stepsVol + platformVol) * qty;
    }
    default:
        return 0;
    }
}

export function calculateMaterials(wetVolumeM3, grade, wastagePercent = 3) {
    const mix = IS_MIX_DESIGN[grade] || IS_MIX_DESIGN.M20;
    const wastage = 1 + (num(wastagePercent) / 100);
    const wetWithWastage = wetVolumeM3 * wastage;
    const dryVolume = wetWithWastage * DRY_VOLUME_FACTOR;

    const cementKg = wetWithWastage * mix.cementKgPerM3;
    const sandM3 = dryVolume * (mix.sandRatio / mix.parts);
    const aggregateM3 = dryVolume * (mix.aggregateRatio / mix.parts);
    const sandKg = sandM3 * SAND_DENSITY_KG_M3;
    const aggregateKg = aggregateM3 * AGGREGATE_DENSITY_KG_M3;
    const cementBags = cementKg / CEMENT_BAG_KG;

    return {
        wet_volume_m3: round(wetVolumeM3, 4),
        wet_with_wastage_m3: round(wetWithWastage, 4),
        dry_volume_m3: round(dryVolume, 4),
        cement_kg: round(cementKg, 2),
        cement_bags: round(cementBags, 2),
        sand_m3: round(sandM3, 4),
        aggregate_m3: round(aggregateM3, 4),
        sand_kg: round(sandKg, 2),
        aggregate_kg: round(aggregateKg, 2),
    };
}

export function calculateLineItem(shapeType, dims, quantity, grade, wastagePercent) {
    const volume = calculateShapeVolume(shapeType, dims, quantity);
    const materials = calculateMaterials(volume, grade, wastagePercent);
    return {
        volume_m3: materials.wet_volume_m3,
        ...materials,
    };
}

export function hasLineMeasurement(shapeType, dimensions = {}, quantity = 1) {
    return Boolean(formatMeasurementSummary(shapeType, dimensions, quantity));
}

/** Summary table qty: manual override, else calculated volume. */
export function getSummaryLineQty(sourceLine = {}, computedLine = {}) {
    const manual = sourceLine.manual_qty_m3;
    if (manual !== '' && manual != null) {
        return num(manual);
    }
    return num(computedLine.volume_m3);
}

/** Summary table rate: manual override, else rate-working rate per m³. */
export function getSummaryLineRate(sourceLine = {}, defaultRatePerM3 = 0) {
    const manual = sourceLine.manual_rate_per_m3;
    if (manual !== '' && manual != null) {
        return num(manual);
    }
    return num(defaultRatePerM3);
}

export function computeSummaryTotals(lines = [], computedLines = [], defaultRatePerM3 = 0) {
    let totalQty = 0;
    let totalAmount = 0;
    lines.forEach((sourceLine, index) => {
        const computedLine = computedLines[index] || {};
        const qty = getSummaryLineQty(sourceLine, computedLine);
        const rate = getSummaryLineRate(sourceLine, defaultRatePerM3);
        totalQty += qty;
        totalAmount += qty * rate;
    });
    const avgRate = totalQty > 0 ? Math.round(totalAmount / totalQty) : 0;
    return { totalQty, totalAmount, avgRate };
}

export function summaryOverridesForDimensions(dimensions = {}, sourceLine = {}) {
    const next = { ...dimensions };
    const qty = sourceLine.manual_qty_m3;
    const rate = sourceLine.manual_rate_per_m3;
    if (qty !== '' && qty != null) {
        next.summary_qty_m3 = num(qty);
    } else {
        delete next.summary_qty_m3;
    }
    if (rate !== '' && rate != null) {
        next.summary_rate_per_m3 = num(rate);
    } else {
        delete next.summary_rate_per_m3;
    }
    return next;
}

export function summaryFieldsFromLine(line = {}) {
    const dims = line.dimensions || {};
    const volume = num(line.volume_m3);
    const manualQty = dims.summary_qty_m3 != null
        ? String(dims.summary_qty_m3)
        : (volume > 0 ? String(volume) : '');
    const manualRate = dims.summary_rate_per_m3 != null
        ? String(dims.summary_rate_per_m3)
        : '';
    return { manual_qty_m3: manualQty, manual_rate_per_m3: manualRate };
}

export function computeEstimatorLine(line = {}) {
    const grade = line.concrete_grade || 'M20';
    const wastage = line.wastage_percent ?? 3;
    const calculated = calculateLineItem(
        line.shape_type,
        line.dimensions,
        line.quantity,
        grade,
        wastage,
    );

    if (hasLineMeasurement(line.shape_type, line.dimensions, line.quantity)) {
        return { ...line, ...calculated, qty_source: 'measurement' };
    }

    const manualQty = num(line.manual_qty_m3);
    if (manualQty > 0) {
        const manualMaterials = calculateMaterials(manualQty, grade, wastage);
        return {
            ...line,
            ...manualMaterials,
            volume_m3: manualMaterials.wet_volume_m3,
            qty_source: 'manual',
        };
    }

    return { ...line, ...calculated, qty_source: 'none' };
}

export function sumLineItems(lines) {
    return lines.reduce((acc, line) => ({
        total_volume_m3: acc.total_volume_m3 + num(line.volume_m3),
        total_cement_kg: acc.total_cement_kg + num(line.cement_kg),
        total_cement_bags: acc.total_cement_bags + num(line.cement_bags),
        total_sand_m3: acc.total_sand_m3 + num(line.sand_m3),
        total_aggregate_m3: acc.total_aggregate_m3 + num(line.aggregate_m3),
        total_sand_kg: acc.total_sand_kg + num(line.sand_kg),
        total_aggregate_kg: acc.total_aggregate_kg + num(line.aggregate_kg),
    }), {
        total_volume_m3: 0,
        total_cement_kg: 0,
        total_cement_bags: 0,
        total_sand_m3: 0,
        total_aggregate_m3: 0,
        total_sand_kg: 0,
        total_aggregate_kg: 0,
    });
}

export function sandKgFromM3(volumeM3) {
    return num(volumeM3) * SAND_DENSITY_KG_M3;
}

export function aggregateKgFromM3(volumeM3) {
    return num(volumeM3) * AGGREGATE_DENSITY_KG_M3;
}

export function formatKg(kg) {
    return `${Number(kg || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;
}

export function getDefaultDimensions(shapeType) {
    const u = (fields) => fields.reduce((acc, key) => {
        acc[key] = '';
        acc[`${key}_unit`] = 'm';
        return acc;
    }, {});

    switch (shapeType) {
    case 'slab_wall_footing':
        return u(['length', 'width', 'height']);
    case 'round_column_hole':
        return u(['diameter', 'height']);
    case 'circular_slab_tube':
        return { ...u(['outer_diameter', 'inner_diameter', 'height']), inner_diameter: '0' };
    case 'curb_gutter':
        return u(['curb_depth', 'gutter_width', 'curb_height', 'flag_thickness', 'length']);
    case 'stairs':
        return { ...u(['run', 'rise', 'width', 'platform_depth']), platform_depth: '0', num_risers: '' };
    default:
        return {};
    }
}

export function ensureDimensionUnits(shapeType, dims = {}) {
    const defaults = getDefaultDimensions(shapeType);
    return { ...defaults, ...dims };
}

export function getShapeFieldLabels(shapeType) {
    switch (shapeType) {
    case 'slab_wall_footing':
        return [
            { key: 'length', label: 'Length (l)', hasUnit: true },
            { key: 'width', label: 'Width (w)', hasUnit: true },
            { key: 'height', label: 'Thickness or Height (h)', hasUnit: true },
        ];
    case 'round_column_hole':
        return [
            { key: 'diameter', label: 'Diameter (d)', hasUnit: true },
            { key: 'height', label: 'Depth / Height (h)', hasUnit: true },
        ];
    case 'circular_slab_tube':
        return [
            { key: 'outer_diameter', label: 'Outer Diameter (D)', hasUnit: true },
            { key: 'inner_diameter', label: 'Inner Diameter (d)', hasUnit: true },
            { key: 'height', label: 'Length / Height (h)', hasUnit: true },
        ];
    case 'curb_gutter':
        return [
            { key: 'curb_depth', label: 'Curb Depth', hasUnit: true },
            { key: 'gutter_width', label: 'Gutter Width', hasUnit: true },
            { key: 'curb_height', label: 'Curb Height', hasUnit: true },
            { key: 'flag_thickness', label: 'Flag Thickness', hasUnit: true },
            { key: 'length', label: 'Length (l)', hasUnit: true },
        ];
    case 'stairs':
        return [
            { key: 'run', label: 'Run (tread depth)', hasUnit: true },
            { key: 'rise', label: 'Rise (step height)', hasUnit: true },
            { key: 'width', label: 'Width (w)', hasUnit: true },
            { key: 'platform_depth', label: 'Platform Depth', hasUnit: true },
            { key: 'num_risers', label: 'Number of Risers', isCount: true },
        ];
    default:
        return [];
    }
}

const MEASUREMENT_SHORT = {
    length: 'l',
    width: 'w',
    height: 'h',
    diameter: 'd',
    outer_diameter: 'D',
    inner_diameter: 'd',
    curb_depth: 'cd',
    gutter_width: 'gw',
    curb_height: 'ch',
    flag_thickness: 'ft',
    run: 'run',
    rise: 'rise',
    platform_depth: 'pd',
    num_risers: 'n',
};

/** Read-only summary built from dimension inputs, e.g. "l=10m, w=45m, h=2m". */
export function formatMeasurementSummary(shapeType, dimensions = {}, quantity = 1) {
    const fields = getShapeFieldLabels(shapeType);
    const parts = fields.map((f) => {
        const raw = dimensions[f.key];
        if (raw === '' || raw === null || raw === undefined) return null;
        const n = parseFloat(raw);
        if (!Number.isFinite(n)) return null;
        const display = Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(3)));
        const sym = MEASUREMENT_SHORT[f.key] || f.key;
        if (f.isCount) return `${sym}=${display}`;
        const unit = dimensions[`${f.key}_unit`] || 'm';
        return `${sym}=${display}${unit}`;
    }).filter(Boolean);

    if (parts.length === 0) return '';
    const qty = Math.max(parseFloat(quantity) || 1, 1);
    if (qty > 1) parts.push(`qty=${qty}`);
    return parts.join(', ');
}

function round(value, decimals) {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}
